"use client";

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/utils/supabase/client' // FIXED: Corrected import path from '@/integrations/supabase/client' to '@/utils/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { TrendingUp, Clock, CheckCircle, Users, Download, RefreshCw } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts'
import { Badge } from '@/components/ui/badge'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { showError } from '@/utils/toast'; // Added showError import
import OpenAIKeyManager from '@/components/OpenAIKeyManager';

const Analytics: React.FC = () => {
  const { user, profile, company } = useAuth();
  const queryClient = useQueryClient();
  const [timePeriod, setTimePeriod] = useState<'last_7_days' | 'last_30_days' | 'last_90_days'>('last_30_days');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const { data: analytics, isLoading, error, refetch } = useQuery({
    queryKey: ['analytics', company?.id, timePeriod],
    queryFn: async () => {
      if (!company?.id) return null;
      
      const { data, error } = await supabase
        .rpc('advanced_analytics', {
          p_company_id: company.id,
          p_time_period: timePeriod
        });
      
      if (error) {
        console.error('Analytics error:', error);
        showError('Failed to load analytics data');
        throw error;
      }
      
      return data?.[0] || null;
    },
    enabled: !!company?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const generatePDFReport = async () => {
    if (!analytics) return;
    
    setIsGeneratingPDF(true);
    try {
      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(20);
      doc.text('Analytics Report', 20, 30);
      
      // Company info
      doc.setFontSize(12);
      doc.text(`Company: ${company?.name || 'N/A'}`, 20, 50);
      doc.text(`Period: ${timePeriod.replace('_', ' ')}`, 20, 60);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 70);
      
      // Analytics data
      const startY = 90;
      const lineHeight = 10;
      
      doc.text(`Revenue: $${analytics.revenue?.toLocaleString() || '0'}`, 20, startY);
      doc.text(`Tasks Completed: ${analytics.tasks_completed || '0'}`, 20, startY + lineHeight);
      doc.text(`Active Users: ${analytics.active_users || '0'}`, 20, startY + lineHeight * 2);
      doc.text(`Avg Session Duration: ${analytics.avg_session_duration?.toFixed(1) || '0'} minutes`, 20, startY + lineHeight * 3);
      doc.text(`Productivity Score: ${analytics.productivity_score?.toFixed(1) || '0'}/100`, 20, startY + lineHeight * 4);
      
      // Save the PDF
      doc.save(`analytics-report-${new Date().toISOString().split('T')[0]}.pdf`);
      
      showSuccess('PDF report generated successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      showError('Failed to generate PDF report');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Analytics</h1>
          <Skeleton className="h-10 w-32" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-destructive mb-4">Error Loading Analytics</h1>
          <p className="text-muted-foreground mb-4">Failed to load analytics data. Please try again.</p>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold mb-4">No Analytics Data</h1>
          <p className="text-muted-foreground">No analytics data available for the selected period.</p>
        </div>
      </div>
    );
  }

  const chartData = [
    {
      name: 'Revenue',
      value: analytics.revenue || 0,
      color: '#3b82f6'
    },
    {
      name: 'Tasks',
      value: analytics.tasks_completed || 0,
      color: '#10b981'
    },
    {
      name: 'Users',
      value: analytics.active_users || 0,
      color: '#f59e0b'
    }
  ];

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
        <div className="flex gap-2">
          <Select value={timePeriod} onValueChange={(value: any) => setTimePeriod(value)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_7_days">Last 7 Days</SelectItem>
              <SelectItem value="last_30_days">Last 30 Days</SelectItem>
              <SelectItem value="last_90_days">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={generatePDFReport} disabled={isGeneratingPDF} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            {isGeneratingPDF ? 'Generating...' : 'Export PDF'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="insights">AI Insights</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Revenue</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${analytics.revenue?.toLocaleString() || '0'}</div>
                <p className="text-xs text-muted-foreground">
                  {timePeriod.replace('_', ' ')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Tasks Completed</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.tasks_completed || '0'}</div>
                <p className="text-xs text-muted-foreground">
                  {timePeriod.replace('_', ' ')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.active_users || '0'}</div>
                <p className="text-xs text-muted-foreground">
                  {timePeriod.replace('_', ' ')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Productivity Score</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.productivity_score?.toFixed(1) || '0'}/100</div>
                <p className="text-xs text-muted-foreground">
                  {timePeriod.replace('_', ' ')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Performance Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Session Duration</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[{name: 'Avg', value: analytics.avg_session_duration || 0}]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="insights" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>AI Insights</CardTitle>
            </CardHeader>
            <CardContent>
              <OpenAIKeyManager />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Analytics Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Configure your analytics preferences and data collection settings.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;